import './EditorBody.css';

import {
  FC,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Image as KonvaImage, Layer, Rect, Stage } from 'react-konva';
import type { KonvaNodeEvents, StageProps } from 'react-konva';
import type Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import CanvasPopoutWindow from '../CanvasPopoutWindow/CanvasPopoutWindow';
import LayoutGrid from '../LayoutGrid/LayoutGrid';
import LayoutObject from '../LayoutObject/LayoutObject';
import PopoutToggleIcon from '../PopoutToggleIcon/PopoutToggleIcon';
import LockItemsToggle from '../LockItemsToggle/LockItemsToggle';
import { useCopyPasteLayoutObjectShortcuts } from '../../../../../hooks/shortcuts/useCopyPasteLayoutObjectShortcuts';
import { useDeleteLayoutObjectShortcut } from '../../../../../hooks/shortcuts/useDeleteLayoutObjectShortcut';
import { useMoveLayoutObjectShortcut } from '../../../../../hooks/shortcuts/useMoveLayoutObjectShortcut';
import { usePopoutWindow } from '../../../../../hooks/usePopoutWindow';
import { useKonvaDragWindowBinding } from '../../../../../hooks/useKonvaDragWindowBinding';
import { useLayoutImage } from '../../../../../hooks/useLayoutImage';
import { useKeyboardShortcutsContext } from '../../../../../hooks/context/useKeyboardShortcutsContext';
import { useLayoutEditorContext } from '../../../../../hooks/context/useLayoutEditorContext';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import { getLayoutEffectiveScale } from '../../../../../utils/layouts/getLayoutEffectiveScale';
import { getLayoutFixedCell } from '../../../../../utils/layouts/getLayoutFixedCell';
import { getLayoutFixedCellIndex } from '../../../../../utils/layouts/getLayoutFixedCellIndex';
import { getLayoutFixedGridSize } from '../../../../../utils/layouts/getLayoutFixedGridSize';
import { normalizeLayoutFontStyle } from '../../../../../utils/layouts/normalizeLayoutFontStyle';
import { snapToGrid } from '../../../../../utils/layouts/snapToGrid';
import { snapToGridInDirection } from '../../../../../utils/layouts/snapToGridInDirection';
import { getObjectRenderOrder } from './utils/getObjectRenderOrder';
import {
  LAYOUT_ARROW_MOVE_STEP,
  LAYOUT_DRAW_MIN_SIZE,
  LAYOUT_OBJECT_SELECTED_STROKE,
  LAYOUT_POPOUT_WINDOW_HEIGHT,
  LAYOUT_POPOUT_WINDOW_WIDTH,
} from '../../../../../constants';

const KonvaStage = Stage as FC<
  StageProps & {
    children?: ReactNode;
    width: number;
    height: number;
    scaleX?: number;
    scaleY?: number;
  }
>;
const KonvaLayer = Layer as FC<
  Konva.LayerConfig & KonvaNodeEvents & { children?: ReactNode }
>;

const EditorBody: FC = () => {
  // Available workspace box (padding excluded) — bestOfScale fits the canvas into it.
  const [workspaceSize, setWorkspaceSize] = useState({ width: 0, height: 0 });
  const workspaceRef = useRef<HTMLDivElement>(null);
  // The scrolling viewport inside the workspace (the popout trigger stays
  // outside of it, pinned to the workspace corner regardless of the canvas
  // scroll).
  const viewportRef = useRef<HTMLDivElement>(null);
  // Rubber band of the click+drag object creation (canvas coordinates);
  // null = not drawing.
  const [drawRect, setDrawRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const drawStartRef = useRef<{ x: number; y: number } | null>(null);
  const { t } = useTranslate();
  // Separate browser window the canvas can pop out into — a plain window, so
  // the user can maximize/snap it across the whole screen.
  const {
    popoutWindow,
    open: openPopoutWindow,
    close: closePopoutWindow,
  } = usePopoutWindow();
  const {
    definition,
    layout,
    canvas,
    objects,
    selectedIndex,
    isFixedMode,
    focusedObjectField,
    selectObject,
    updateObject,
    addObject,
    removeObject,
    moveObject,
    copySelectedObject,
    pasteObject,
    isObjectCopied,
    clearCopiedObjectMark,
    isObjectDirty,
    isObjectNew,
    setFocusedObjectField,
  } = useLayoutEditorContext();

  // Last known cursor position in canvas coordinates; null = cursor outside
  // the canvas (paste then falls back to the cascade below the source).
  const lastPointerPositionRef = useRef<{ x: number; y: number } | null>(null);

  // Popout window title mirrors the editor header: definition caption (or the
  // generic editor title) plus the layout name.
  const popoutTitle = [
    definition?.caption ?? t('layoutEditor.title'),
    layout?.name,
  ]
    .filter(Boolean)
    .join(' – ');

  // Keeps the title in sync should the caption/name change while popped out.
  useEffect(() => {
    if (!popoutWindow) {
      return;
    }

    popoutWindow.document.title = popoutTitle;
  }, [popoutWindow, popoutTitle]);

  const { attachShortcutWindow } = useKeyboardShortcutsContext();

  useDeleteLayoutObjectShortcut({
    scopeRef: workspaceRef,
    selectedIndex,
    onDelete: removeObject,
  });

  // Paste at the canvas cursor; outside the canvas the clone cascades below
  // the source.
  const handlePasteObject = (): void => {
    const position = lastPointerPositionRef.current;

    if (position) {
      pasteObject(Math.round(position.x), Math.round(position.y));
      return;
    }

    pasteObject();
  };

  useCopyPasteLayoutObjectShortcuts({
    scopeRef: workspaceRef,
    selectedIndex,
    onCopy: copySelectedObject,
    onPaste: handlePasteObject,
  });

  // Arrow keys move the selected object: with snapGrid on, the top-left corner
  // jumps to the next grid dot in that direction; otherwise by one pixel.
  // Fixed mode has no geometry — arrows shift the object in the list instead.
  // No-op in locked layouts.
  const handleMoveObject = (dx: -1 | 0 | 1, dy: -1 | 0 | 1): void => {
    if (selectedIndex === null || canvas.lockItems) {
      return;
    }

    if (isFixedMode) {
      const step = dx !== 0 ? dx : dy;
      moveObject(selectedIndex, selectedIndex + step);
      return;
    }

    const object = objects[selectedIndex];

    if (!object) {
      return;
    }

    const x = object.x ?? 0;
    const y = object.y ?? 0;
    const stepX = canvas.gridX ?? 0;
    const stepY = canvas.gridY ?? 0;

    let nextX = x;
    let nextY = y;

    const snapsX = !!canvas.snapGrid && stepX > 0;
    const snapsY = !!canvas.snapGrid && stepY > 0;

    // The moved axis walks to the next boundary in the pressed direction; the
    // other axis aligns to its nearest boundary — the corner always lands on a
    // grid dot (boundary intersection), never just on a line.
    if (dx !== 0) {
      nextX = snapsX
        ? snapToGridInDirection(x, stepX, canvas.gapX ?? 0, dx)
        : x + dx * LAYOUT_ARROW_MOVE_STEP;
    } else if (snapsX) {
      nextX = snapToGrid(x, stepX, canvas.gapX ?? 0);
    }
    if (dy !== 0) {
      nextY = snapsY
        ? snapToGridInDirection(y, stepY, canvas.gapY ?? 0, dy)
        : y + dy * LAYOUT_ARROW_MOVE_STEP;
    } else if (snapsY) {
      nextY = snapToGrid(y, stepY, canvas.gapY ?? 0);
    }

    // Keep the object inside the canvas.
    nextX = Math.min(
      Math.max(nextX, 0),
      Math.max(0, canvas.width - (object.width ?? 0))
    );
    nextY = Math.min(
      Math.max(nextY, 0),
      Math.max(0, canvas.height - (object.height ?? 0))
    );

    if (nextX === x && nextY === y) {
      return;
    }

    updateObject(selectedIndex, { x: nextX, y: nextY });
  };

  useMoveLayoutObjectShortcut({
    scopeRef: workspaceRef,
    selectedIndex,
    onMove: handleMoveObject,
  });

  // Konva drag&drop listens on the main window only — mirror its listeners on
  // the popout window, or a drag started there never ends (the object would
  // chase the pointer once it re-enters the main window).
  useKonvaDragWindowBinding(popoutWindow);

  // The popout window has its own event loop — the shortcut dispatcher
  // (Delete) must listen there too while the canvas is popped out.
  useEffect(() => {
    if (!popoutWindow) {
      return;
    }

    return attachShortcutWindow(popoutWindow);
  }, [popoutWindow, attachShortcutWindow]);

  // Canvas background bitmap (base64 from the API), drawn 1:1 from the top-left
  // corner; overflow past the canvas edge is clipped by the stage itself.
  const backgroundImage = useLayoutImage(canvas.backgroundImage);

  // Fixed layouts come without canvas dimensions — the canvas is exactly the
  // grid extent (columns/rows of gridX × gridY buttons with gaps) and
  // bestOfScale fits it into the workspace. Free mode keeps the API canvas.
  const effectiveCanvas = useMemo(() => {
    if (!isFixedMode) {
      return canvas;
    }

    return {
      ...canvas,
      ...getLayoutFixedGridSize(canvas, objects.length),
    };
  }, [canvas, isFixedMode, objects.length]);

  // Canvas zoom: bestOfScale fit into the workspace, or the scaleX/scaleY values.
  const scale = useMemo(
    () =>
      getLayoutEffectiveScale(
        effectiveCanvas,
        workspaceSize.width,
        workspaceSize.height
      ),
    [effectiveCanvas, workspaceSize]
  );

  // Fixed mode: the canvas carries the default styling of the buttons (the
  // objects are data-only) — an object's own values would win should they
  // ever arrive. Free mode styles from the object data alone.
  const fixedStyleDefaults = useMemo(() => {
    if (!isFixedMode) {
      return undefined;
    }

    return {
      backgroundColor: canvas.objectBackgroundColor,
      fontColor: canvas.fontColor,
      fontFamily: canvas.fontFamily,
      fontSize: canvas.fontSize,
      fontStyle: normalizeLayoutFontStyle(canvas.fontStyle),
    };
  }, [canvas, isFixedMode]);

  // Re-attached when the workspace moves between the page and the popout
  // window (the portal remounts it into a different document).
  useLayoutEffect(() => {
    const viewportElement = viewportRef.current;

    if (!viewportElement) {
      return;
    }

    // contentRect = the box available for the canvas (padding excluded).
    const observer = new ResizeObserver(([entry]) => {
      setWorkspaceSize({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });
    observer.observe(viewportElement);

    return () => observer.disconnect();
  }, [popoutWindow]);

  const handleStagePointerDown = (
    event: KonvaEventObject<MouseEvent | TouchEvent>
  ): void => {
    // Konva prevents the default mousedown, so a click into the canvas never
    // blurs the focused form input — drop the field-focus overlay explicitly.
    setFocusedObjectField(null);

    // Any click into the canvas drops the "copied" frame (the clipboard
    // content itself stays usable).
    clearCopiedObjectMark();

    // Click on empty canvas deselects.
    if (event.target !== event.target.getStage()) {
      return;
    }

    selectObject(null);

    // Left-button press on empty canvas may start drawing a new object
    // (click + drag, like the desktop Manažer); a plain click stays a click.
    const isLeftButton =
      !('button' in event.evt) || (event.evt as MouseEvent).button === 0;
    const position = event.target.getStage()?.getRelativePointerPosition();

    if (!isLeftButton || !position || isFixedMode || canvas.lockItems) {
      return;
    }

    drawStartRef.current = { x: position.x, y: position.y };
  };

  const handleStagePointerMove = (
    event: KonvaEventObject<MouseEvent | TouchEvent>
  ): void => {
    const position = event.target.getStage()?.getRelativePointerPosition();

    // Ctrl+V pastes at the last known cursor position.
    lastPointerPositionRef.current = position ?? null;

    const start = drawStartRef.current;

    if (!start || !position) {
      return;
    }

    setDrawRect({
      x: Math.min(start.x, position.x),
      y: Math.min(start.y, position.y),
      width: Math.abs(position.x - start.x),
      height: Math.abs(position.y - start.y),
    });
  };

  const handleStagePointerUp = (): void => {
    const rect = drawRect;

    drawStartRef.current = null;
    setDrawRect(null);

    // Tiny rects are just sloppy clicks — deselect already happened on press.
    if (
      !rect ||
      rect.width < LAYOUT_DRAW_MIN_SIZE ||
      rect.height < LAYOUT_DRAW_MIN_SIZE
    ) {
      return;
    }

    addObject(
      Math.round(rect.x),
      Math.round(rect.y),
      Math.round(rect.width),
      Math.round(rect.height)
    );
  };

  // The pointer left the stage — cancel the rubber band and forget the cursor
  // position (paste falls back to the cascade).
  const handleStagePointerLeave = (): void => {
    drawStartRef.current = null;
    setDrawRect(null);
    lastPointerPositionRef.current = null;
  };

  const handleStageDoubleClick = (
    event: KonvaEventObject<MouseEvent | TouchEvent>
  ): void => {
    // Double click on empty canvas adds a new object at the pointer position.
    if (event.target !== event.target.getStage()) {
      return;
    }

    // Relative position — accounts for the stage zoom (canvas coordinates).
    const position = event.target.getStage()?.getRelativePointerPosition();

    if (!position) {
      return;
    }

    addObject(Math.round(position.x), Math.round(position.y));
  };

  // Fixed mode: the dropped object's center picks the target grid cell — the
  // object moves to that position in the list.
  const handleReorderDrop = (
    index: number,
    centerX: number,
    centerY: number
  ): void => {
    const targetIndex = getLayoutFixedCellIndex(
      effectiveCanvas,
      objects.length,
      centerX,
      centerY
    );

    moveObject(index, targetIndex);
  };

  const handleTogglePopoutWindow = (): void => {
    if (popoutWindow) {
      closePopoutWindow();
      return;
    }

    openPopoutWindow(
      LAYOUT_POPOUT_WINDOW_WIDTH,
      LAYOUT_POPOUT_WINDOW_HEIGHT,
      popoutTitle
    );
  };

  // The whole workspace renders either in place or portaled into the popout
  // window — the Konva stage and all editor interactions move with it.
  const workspace = (
    <div className="layout-editor-workspace" ref={workspaceRef}>
      <div className="layout-editor-workspace-actions">
        <LockItemsToggle />
        <button
          type="button"
          className="layout-editor-popout-button"
          title={t(
            popoutWindow
              ? 'layoutEditor.popoutClose'
              : 'layoutEditor.popoutOpen'
          )}
          onClick={handleTogglePopoutWindow}
        >
          <PopoutToggleIcon variant={popoutWindow ? 'contract' : 'open'} />
        </button>
      </div>
      <div className="layout-editor-canvas-viewport" ref={viewportRef}>
        <div className="layout-editor-canvas-scroll">
          <KonvaStage
            width={effectiveCanvas.width * scale.x}
            height={effectiveCanvas.height * scale.y}
            scaleX={scale.x}
            scaleY={scale.y}
            onMouseDown={handleStagePointerDown}
            onTouchStart={handleStagePointerDown}
            onMouseMove={handleStagePointerMove}
            onTouchMove={handleStagePointerMove}
            onMouseUp={handleStagePointerUp}
            onTouchEnd={handleStagePointerUp}
            onMouseLeave={handleStagePointerLeave}
            onDblClick={handleStageDoubleClick}
            onDblTap={handleStageDoubleClick}
          >
            <KonvaLayer>
              <Rect
                width={effectiveCanvas.width}
                height={effectiveCanvas.height}
                fill={effectiveCanvas.backgroundColor}
                listening={false}
              />
              {backgroundImage && (
                <KonvaImage image={backgroundImage} listening={false} />
              )}
              <LayoutGrid canvas={effectiveCanvas} />
              {getObjectRenderOrder(objects.length, selectedIndex).map(
                (index) => (
                  <LayoutObject
                    key={index}
                    object={objects[index]}
                    index={index}
                    isSelected={index === selectedIndex}
                    canvasWidth={effectiveCanvas.width}
                    canvasHeight={effectiveCanvas.height}
                    snapEnabled={!!canvas.snapGrid}
                    gridX={canvas.gridX ?? 0}
                    gridY={canvas.gridY ?? 0}
                    gapX={canvas.gapX ?? 0}
                    gapY={canvas.gapY ?? 0}
                    scaleX={scale.x}
                    scaleY={scale.y}
                    fixedCell={
                      isFixedMode
                        ? getLayoutFixedCell(effectiveCanvas, index)
                        : undefined
                    }
                    styleDefaults={fixedStyleDefaults}
                    isDraggable={!canvas.lockItems}
                    isResizable={!canvas.lockItems && !isFixedMode}
                    eventWindow={popoutWindow}
                    isDirty={isObjectDirty(index)}
                    isNew={isObjectNew(index)}
                    isCopied={isObjectCopied(index)}
                    focusedField={
                      index === selectedIndex ? focusedObjectField : null
                    }
                    onSelect={selectObject}
                    onChange={updateObject}
                    onReorderDrop={isFixedMode ? handleReorderDrop : undefined}
                  />
                )
              )}
              {drawRect && (
                // Rubber band of the click+drag creation.
                <Rect
                  x={drawRect.x}
                  y={drawRect.y}
                  width={drawRect.width}
                  height={drawRect.height}
                  stroke={LAYOUT_OBJECT_SELECTED_STROKE}
                  strokeWidth={1}
                  dash={[4, 4]}
                  listening={false}
                />
              )}
            </KonvaLayer>
          </KonvaStage>
        </div>
      </div>
    </div>
  );

  if (!popoutWindow) {
    return workspace;
  }

  return (
    <CanvasPopoutWindow popoutWindow={popoutWindow} onClose={closePopoutWindow}>
      {workspace}
    </CanvasPopoutWindow>
  );
};

export default EditorBody;
